import { useState, useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import pfp from './../image.jpeg'
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-scroll'
import { TypeAnimation } from 'react-type-animation'
import AOS from 'aos'
import 'aos/dist/aos.css'

const navigation = [
  { name: 'Skills', id: 'skills' },
  { name: 'Projects', id: 'projects' },
  { name: 'Work Experience', id: 'experience' },
  { name: 'Pricing', id: 'pricing' },
  { name: 'Testimonials', id: 'testimonials' },
  { name: 'Contact Me', id: 'contact' },
]

export default function Hero() {
  const [theme, setTheme] = useState(
    localStorage.getItem('theme') ? localStorage.getItem('theme') : 'light'
  )

  useEffect(() => {
    localStorage.setItem('theme', theme)
    document.querySelector('html').setAttribute('data-theme', theme)
  }, [theme])

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleToggle = (e) => {
    e.target.checked ? setTheme('dark') : setTheme('light')
  }

  useEffect(() => {
    AOS.init({ duration: 2000 })
  }, [])

  return (
    <div>
      <header className="fixed inset-x-0 top-0 z-50 bg-base-300 shadow-2xl shadow-neutral">
        <nav
          className="flex items-center justify-between p-6 lg:px-8"
          aria-label="Global"
        >
          <div className="flex lg:flex-1">
            <Link
              className="text-sm font-semibold leading-6 cursor-pointer"
              to={'home'}
              spy={true}
              smooth={true}
              offset={50}
              duration={500}
            >
              Me
            </Link>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            {navigation.map((item) => (
              <Link
                key={item.id}
                to={item.id}
                spy={true}
                smooth={true}
                offset={50}
                duration={500}
                className="text-sm font-semibold leading-6 cursor-pointer"
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            <label className="swap swap-rotate">
              <input
                type="checkbox"
                className="hidden"
                onChange={handleToggle}
                checked={theme === 'dark'}
              />
              <SunIcon className="swap-off fill-current w-6 h-6" />
              <MoonIcon className="swap-on fill-current w-6 h-6" />
            </label>
          </div>
        </nav>
      </header>

      <div className="py-16" id="home">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div
            className="grid lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 py-16"
            data-aos="zoom-in"
          >
            {/* Profile Image */}
            <div className="grid justify-center items-center">
              <img
                src={pfp}
                alt="Akhtar Abbas"
                className="w-96 h-96 rounded-full object-cover drop-shadow-2xl"
              />
            </div>

            {/* Hero Text */}
            <div className="grid justify-center items-center mt-3">
              <div className="text-center">
                <div className="text-2xl tracking-tight sm:text-3xl">
                  Hello, It's
                </div>
              </div>
              <div className="text-center">
                <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">
                  Akhtar Abbas
                </h1>
                <p className="mt-6 text-2xl leading-8">
                  <TypeAnimation
                    sequence={[
                      "I'm a Full Stack Developer",
                      2000,
                      "I'm a Desktop Developer",
                      2000,
                      "I'm a Mobile Application Developer",
                      2000,
                      "I'm a Web Developer",
                      2000,
                    ]}
                    speed={50}
                    repeat={Infinity}
                  />
                </p>

                {/* Updated Shortened Description */}
                <div className="mt-10 flex items-center justify-center gap-x-6">
                  <p>
                    Experienced Full Stack Developer with over a decade of
                    expertise in building robust and scalable web, mobile, and
                    desktop applications. Skilled in Android development using
                    Xamarin Forms and web development with technologies like
                    Python, Django, ASP.NET, and ReactJS. Proficient in backend
                    technologies, including WCF, ASMX services, and RESTful
                    APIs. Strong database management capabilities in MS SQL
                    Server, PostgreSQL, and ClickHouse DB. Adept at integrating
                    third-party controls like Telerik and Infragistics and
                    developing front-end solutions using JavaScript, HTML, CSS,
                    and ReactJS.
                  </p>
                </div>

                {/* Social Links (No Changes in Icons) */}
                <div className="mt-10 flex items-center justify-center gap-x-4">
                  <a
                    href="https://www.linkedin.com/in/akhtarabbas909/"
                    target="_blank"
                    className="btn btn-outline btn-square"
                    rel="noreferrer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      className="h-8 w-8"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
                    </svg>
                  </a>

                  {/* ✅ Updated GitHub Icon */}
                  <a
                    href="https://github.com/akhtarabbas-909"
                    target="_blank"
                    className="btn btn-outline btn-square"
                    rel="noreferrer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.866-.014-1.7-2.782.604-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.607.069-.607 1.004.071 1.531 1.031 1.531 1.031.892 1.528 2.341 1.087 2.91.832.092-.647.35-1.087.636-1.337-2.22-.253-4.555-1.11-4.555-4.941 0-1.091.39-1.984 1.031-2.682-.103-.253-.447-1.272.098-2.65 0 0 .84-.269 2.75 1.026a9.564 9.564 0 0 1 2.5-.337c.848.004 1.705.115 2.5.337 1.909-1.295 2.748-1.026 2.748-1.026.547 1.378.203 2.397.1 2.65.642.698 1.03 1.591 1.03 2.682 0 3.841-2.337 4.685-4.564 4.935.36.31.678.92.678 1.852 0 1.337-.012 2.415-.012 2.743 0 .268.18.58.688.481C19.138 20.164 22 16.418 22 12c0-5.523-4.477-10-10-10z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
